<template>
  <div
    v-if="story"
    class="bg-white flex flex-col flex-shrink-0 justify-between
      overflow-hidden rounded-lg text-gray-900"
  >
    <div>
      <span
        v-if="story.seasonPass"
        class="bg-green-400 block font-bold leading-snug px-2 py-1
          text-center text-sm uppercase w-full"
        v-text="'Season Pass'"
      ></span>
      <div class="p-4">
        <h3 class="h4 mb-2">{{ story.title }}</h3>
        <p class="text-sm text-gray-600 mt-0">
          By: <Contributor :id="story.credits.author" />
        </p>
      </div>
    </div>
    <div>
      <ul class="bg-gray-300 border-gray-400 border-t-2 flex items-center text-sm">
        <li class="px-4 flex-1 text-lg text-center font-bold" v-text="totalRatings.score || 0"></li>
        <li
          v-for="(info, rating, index) in ratingsList"
          :key="index"
          class="border-gray-400 border-l-2 flex-1"
        >
          <button
            :class="{ 'bg-gray-100': userRating == rating }"
            @click="selectRating(rating)"
            class="flex hover:bg-gray-100 items-center justify-center mb-0 p-2 w-full"
            v-tooltip.top-center="info.text"
          >
            <span
              class="inline-block leading-none text-lg mr-1"
              v-text="info.icon"
            ></span>
            <span
              :class="`text-gray-${ info.count > 0 ? 9: 5}00`"
              v-text="info.count"
            ></span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Contributor from './Contributor.vue';

export default {
  components: {
    Contributor
  },
  props: {
    id: {
      type: String
    },
  },
  created() {
    const { id, fetchStory } = this;

    fetchStory(id);
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'users/loggedInUser',
      stories: 'stories/items'
    }),
    story() {
      return this.stories[this.id];
    },
    userRating() {
      const { loggedInUser } = this;
      if (!loggedInUser) return null;

      const { ratings } = this.story;
      return ratings && ratings[loggedInUser.id];
    },
    ratingsList() {
      const { totalRatings } = this;

      return {
        1: {
          icon: '😕',
          text: 'Not My Thing',
          count: totalRatings['1'] || 0
        },
        2: {
          icon: '🤔',
          text: 'Iffy',
          count: totalRatings['2'] || 0
        },
        3: {
          icon: '🙂',
          text: 'Liked It',
          count: totalRatings['3'] || 0
        },
        4: {
          icon: '😲',
          text: 'Noteworthy',
          count: totalRatings['4'] || 0
        },
        5: {
          icon: '😱',
          text: 'OMG!',
          count: totalRatings['5'] || 0
        }
      };
    },
    totalRatings() {
      // move to cloud function
      const { ratings } = this.story;

      if (!ratings) return {};

      const totals = {};
      const rates = Object.values(ratings);

      [1, 2, 3, 4, 5].forEach((r) => {
        totals[r] = rates.filter(v => Math.floor(v) === r).length;
      });

      // get weighted average
      const total = rates.reduce((a, c) => a + c);
      const count = rates.length;

      totals.score = parseFloat((total / count).toFixed(2));

      return totals;
    }
  },
  methods: {
    ...mapActions({
      fetchEpisode: 'episodes/fetchEpisode',
      fetchStory: 'stories/fetchStory',
      updateStory: 'stories/updateStory',
      updateUser: 'users/updateUser',
    }),
    getWeightedAverage(obj) {
      // Not In Use, Original Script
      // Kept as a backup

      const count = Object.values(obj).reduce((a, c) => a + c);

      // eslint-disable-next-line arrow-body-style
      const total = Object.entries(obj).reduce((a, entry) => {
        return a + entry.reduce((k, v) => k * v);
      }, 0);

      return parseFloat((total / count).toFixed(2));
    },
    selectRating(rating) {
      const {
        loggedInUser,
        id,
        updateStory,
        updateUser
      } = this;

      if (!loggedInUser) {
        // eslint-disable-next-line
        if (window.confirm('Please sign-in to rate stories')) {
          this.$router.push('login');
        }

        return;
      }

      updateUser({
        id: loggedInUser.id,
        data: {
          [`ratings.${id}`]: parseInt(rating, 10)
        }
      });

      updateStory({
        id,
        data: {
          [`ratings.${loggedInUser.id}`]: parseInt(rating, 10)
        }
      });
    }
  }
};
</script>

<style lang="scss">
.tooltip {
  display: block !important;
  z-index: 10000;

  .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
  }

  &[x-placement^="top"] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="bottom"] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="right"] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^="left"] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity .15s, visibility .15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity .15s;
  }
}
</style>
